import { Box } from '@mui/material';
import { forwardRef, memo, ReactNode } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import InfiniteLoader from 'react-window-infinite-loader';
import { OuterElementContext } from './VirtualListOuterElementContext';
import { OuterElementType } from './VirtualListOuterElementType';

interface VirtualAutocompleteListboxProps
  extends React.HTMLAttributes<HTMLElement> {
  VirtualListRow: (props: ListChildComponentProps) => JSX.Element;
  itemSize?: number;
  itemsToDisplay?: number;
  itemOffset?: number;
  loadMoreItems?: (
    startIndex: number,
    stopIndex: number,
  ) => void | Promise<void>;
  hasNextPage?: boolean;
  renderListHeader?: () => ReactNode;
}

export const VirtualAutocompleteListbox = memo(
  forwardRef<HTMLDivElement, VirtualAutocompleteListboxProps>(
    function VirtualAutocompleteListbox(props, ref) {
      const {
        children,
        VirtualListRow,
        itemSize = 48,
        itemsToDisplay = 8,
        itemOffset = 16,
        loadMoreItems = () => {},
        hasNextPage = false,
        renderListHeader,
        ...other
      } = props;
      const itemData: React.ReactChild[] = [];
      (children as React.ReactChild[]).forEach(
        (item: React.ReactChild & { children?: React.ReactChild[] }) => {
          itemData.push(item);
          itemData.push(...(item.children || []));
        },
      );

      const itemCount = hasNextPage ? itemData.length + 1 : itemData.length;

      const getChildSize = (child: React.ReactChild) => {
        return itemSize;
      };

      const listHeight = (() => {
        if (itemCount > 8) {
          return itemsToDisplay * itemSize - itemOffset;
        }
        return (
          itemData.map(getChildSize).reduce((a, b) => a + b, 0) + itemOffset
        );
      })();

      return (
        <Box component="div" ref={ref}>
          <OuterElementContext.Provider value={other}>
            {renderListHeader?.()}
            <InfiniteLoader
              isItemLoaded={(index) => !hasNextPage || index < itemData.length}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered, ref: variableRef }) => {
                return (
                  <VariableSizeList
                    itemData={itemData}
                    height={listHeight}
                    width="100%"
                    ref={variableRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    onItemsRendered={onItemsRendered}
                    itemCount={itemCount}
                  >
                    {VirtualListRow}
                  </VariableSizeList>
                );
              }}
            </InfiniteLoader>
          </OuterElementContext.Provider>
        </Box>
      );
    },
  ),
);
